/**
 * useShoppingList Hook
 *
 * Thin wrapper around the Zustand shopping list store. Exposes state and
 * actions, ensuring the list is fetched on first use.
 */

import { useEffect } from 'react';
import { useShoppingListStore } from '@/stores/shopping-list/useShoppingListStore';
import type {
  AddToShoppingListRequest,
  ShoppingList,
  UpdateShoppingListItemRequest,
} from '@/types/shoppingList';

export interface UseShoppingListReturn {
  shoppingList: ShoppingList | null;
  loading: boolean;
  mutating: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  addItems: (request: AddToShoppingListRequest) => Promise<void>;
  updateItem: (itemId: string, update: UpdateShoppingListItemRequest) => Promise<void>;
  toggleItemCheck: (itemId: string, isChecked: boolean) => Promise<void>;
  updateQuantity: (itemId: string, quantity: number) => Promise<void>;
  deleteItem: (itemId: string) => Promise<void>;
  clearList: () => Promise<void>;
  emailList: (email: string) => Promise<void>;
}

/**
 * Hook for managing the shopping list lifecycle.
 */
export const useShoppingList = (): UseShoppingListReturn => {
  const shoppingList = useShoppingListStore(state => state.shoppingList);
  const loading = useShoppingListStore(state => state.loading);
  const mutating = useShoppingListStore(state => state.mutating);
  const error = useShoppingListStore(state => state.error);
  const initialized = useShoppingListStore(state => state.initialized);
  const refresh = useShoppingListStore(state => state.fetchList);
  const addItems = useShoppingListStore(state => state.addItems);
  const updateItem = useShoppingListStore(state => state.updateItem);
  const toggleItemCheck = useShoppingListStore(state => state.toggleItemCheck);
  const updateQuantity = useShoppingListStore(state => state.updateQuantity);
  const deleteItem = useShoppingListStore(state => state.deleteItem);
  const clearList = useShoppingListStore(state => state.clearList);
  const emailList = useShoppingListStore(state => state.emailList);

  useEffect(() => {
    if (!initialized) {
      void refresh();
    }
  }, [initialized, refresh]);

  return {
    shoppingList,
    loading,
    mutating,
    error,
    refresh,
    addItems,
    updateItem,
    toggleItemCheck,
    updateQuantity,
    deleteItem,
    clearList,
    emailList,
  };
};

export default useShoppingList;
